import type { ProductionOrderListRow } from '@api/work-order';

import { openRemaining } from '@components/containers/vendorHome/orderTotals';
import { groupLinesBy } from '@utils/rowTree';

/**
 * One shape for both levels of the grid.
 *
 * DataSheet flattens expanded children into the same virtualized list, so a child must be the
 * same row type as its parent — every column renderer then works unchanged at both levels. That
 * constraint is why an order and a SKU line share these fields rather than having their own.
 *
 * `kind` is what the renderers branch on. Without it a line and an order are indistinguishable
 * once flattened, and the columns that only make sense on one of them have no way to tell.
 */
export interface ProductionOrderGridRow {
    key: string;
    kind: 'order' | 'line';

    /** PO-2026-0001 for an order, WO-2026-0022 for a line. */
    code: string;
    /**
     * Who or what the row is about: the manufacturer for an order, the product for a line.
     *
     * One column rather than two because they occupy the same place in the reader's eye —
     * "SportsTex India" and "Dri-FIT Jersey" both answer "what am I looking at".
     */
    subject: string;
    /**
     * WHO is making it, by id: the same on an order and on each of its lines, so one
     * manufacturer filter reads both levels. Blank on a product row that gathers several.
     */
    productionLocationId: string;
    dueDate: string | null;
    /**
     * THE ORDER'S status on an order row, THE LINE'S on a line row.
     *
     * They differ legitimately — an order is ISSUED while one SKU is cancelled — which is why
     * the column is labelled by level rather than just "Status". Reading one as the other is
     * what made an issued order look like a draft.
     */
    status: string;
    quantity: number;
    /**
     * How much has actually left the site against this row.
     *
     * ON AN ORDER IT IS THE SUM OF ITS LINES, because there is no production-order id on a
     * movement and there need not be — the work order is the finest grain anybody states, and
     * the order above it rolls up from that.
     *
     * Correct by construction for a consignment carrying batches against several orders: the
     * figure comes from per-batch ledger rows each naming their own work order, never from a
     * shipment header, which such a consignment leaves empty.
     */
    dispatched: number;
    /**
     * Where the goods are NOW: cleared and still here, and declared but unruled.
     *
     * Summed from the lines on an order row, the same way `dispatched` is — there is no
     * order-grained QC record either. Neither is cumulative: a batch that passed and then
     * shipped has left `qcApproved` for `dispatched`.
     */
    qcApproved: number;
    qcPending: number;
    /** Ordered minus shipped: what is still owed. Floored per line — see openQuantity. */
    openQuantity: number;

    /**
     * When the order was placed. A line carries its order's date too: it is not placed
     * separately, but a line filtered out of a download on its own must still say when.
     *
     * Distinct from `dueDate`, which is when it lands. A list is read for both: how long has
     * this been outstanding, and when does it arrive.
     */
    orderDate: string | null;
    /**
     * What the order is worth, GST included, as the server stored it.
     *
     * A STRING because it is a decimal, and null because it may genuinely be absent: an order
     * raised before pricing existed has no value, one priced at nothing has "0.00", and the
     * column must be able to say which. Order rows only — line rates live on the work order,
     * not the header, and inventing a per-line share here would be arithmetic nobody asked for.
     */
    total: string | null;
    currency: string | null;
    /**
     * How many SKUs an order holds — and, grouped by product, how many ORDERS want this one.
     * Null on a line, where the column is meaningless either way.
     */
    skuCount: number | null;
    /**
     * WHICH SKU a line is for, as opposed to what it is called.
     *
     * `subject` carries the display name, which is not safe to group on: two SKUs can share a
     * name and folding on what is printed would merge two commitments. Empty on an order row.
     */
    productId: string;
    /**
     * What that SKU is called, kept apart from `subject`.
     *
     * `subject` is the row's headline and means different things at different levels — the
     * manufacturer on an order, the product on a line, and the manufacturer again on a line
     * once the grid is grouped by product. This one only ever means the product, which is what
     * the SKU grouping needs to name its rows after `subject` has been rewritten.
     */
    productName: string;
    sentAt: string | null;
    sentTo: string | null;
    hasDocument: boolean;
    /** Later than `sentAt` means the order was edited after it went out. Orders only. */
    documentGeneratedAt: string | null;
    /** The id an action needs. Null on a line, which cannot be sent on its own. */
    orderId: string | null;
    /**
     * Text search should match but no column shows: the line codes and product names under an
     * order, and the order and work-order codes gathered under a product once grouped by SKU.
     *
     * DataSheet searches TOP-LEVEL rows and only walks into children once expanded, so what
     * sits a level down stops being findable without this. Typing a SKU name has to find the
     * orders that want it, and a work-order code the order it is a line of.
     */
    searchBlob?: string;
    /** The id a line action needs. Null on an order, which is not a work order. */
    workOrderId: string | null;

    lines?: ProductionOrderGridRow[];
}

/**
 * Build the order-grained tree the grid renders.
 *
 * Orders arrive newest first from the server and that order is kept — a production order list is
 * read as "what have we commissioned lately", not alphabetically.
 */
/** Rolled up from rows already built — what the SKU fold adds up. */
const sumRows = (
    rows: ProductionOrderGridRow[],
    pick: (row: ProductionOrderGridRow) => number,
) => rows.reduce((total, row) => total + (pick(row) || 0), 0);

export function buildProductionOrderRows(
    orders: ProductionOrderListRow[],
): ProductionOrderGridRow[] {
    return orders.map((order) => ({
        key: `order:${order.id}`,
        kind: 'order' as const,
        code: order.code,
        subject: order.productionLocationName,
        productionLocationId: order.productionLocationId,
        dueDate: order.expectedCompletionDate || null,
        orderDate: order.orderDate,
        total: order.total,
        currency: order.currency,
        status: order.status,
        quantity: order.totalQuantity,
        dispatched: order.lines.reduce((sum, line) => sum + (line.dispatched ?? 0), 0),
        qcApproved: order.lines.reduce((sum, line) => sum + (line.qcApproved ?? 0), 0),
        qcPending: order.lines.reduce((sum, line) => sum + (line.qcPending ?? 0), 0),
        // Summed from the lines rather than ordered-minus-shipped at the order level: the
        // per-line floor is what stops an over-shipped SKU cancelling a short one.
        openQuantity: order.lines.reduce(
            (sum, line) => sum + openRemaining(line.status, line.quantity ?? 0, line.produced ?? 0),
            0,
        ),
        skuCount: order.lineCount,
        productId: '',
        productName: '',
        sentAt: order.sentAt,
        sentTo: order.sentTo,
        hasDocument: order.hasDocument,
        documentGeneratedAt: order.documentGeneratedAt ?? null,
        orderId: order.id,
        workOrderId: null,
        searchBlob: order.lines
            .map((line) => `${line.code} ${line.productName ?? line.productId}`)
            .join(' '),
        lines: order.lines.map((line) => ({
            // Prefixed so a parent and a child can never collide on a key, which DataSheet
            // requires and which is otherwise easy to get wrong when both carry codes.
            key: `line:${line.workOrderId}`,
            kind: 'line' as const,
            code: line.code,
            subject: line.productName ?? line.productId,
            productionLocationId: order.productionLocationId,
            dueDate: line.dueDate,
            orderDate: order.orderDate,
            total: null,
            currency: null,
            status: line.status,
            quantity: line.quantity,
            dispatched: line.dispatched ?? 0,
            qcApproved: line.qcApproved ?? 0,
            qcPending: line.qcPending ?? 0,
            openQuantity: openRemaining(line.status, line.quantity ?? 0, line.produced ?? 0),
            skuCount: null,
            productId: line.productId,
            productName: line.productName ?? line.productId,
            sentAt: null,
            sentTo: null,
            hasDocument: false,
            documentGeneratedAt: null,
            orderId: null,
            workOrderId: line.workOrderId,
        })),
    }));
}

/**
 * Whether a row can be cancelled from the grid, and what the button should say.
 *
 * AN ORDER CANCELS AS A WHOLE — its open lines go with it — so the verb differs by level, and
 * the reader should know which they are about to do. Neither is offered once the row is
 * finished: a received line or a completed order has nothing left to cancel, and a cancelled
 * one already has been. An order with anything shipped is not offered either: the server
 * refuses it, and a button the server will refuse is worse than none.
 */
export function resolveCancelAction(row: {
    kind: 'order' | 'line';
    status: string;
    dispatched?: number;
}): { label: string; available: boolean } {
    if (row.kind === 'order') {
        const finished = row.status === 'CANCELLED' || row.status === 'COMPLETED';
        return { label: 'Cancel order', available: !finished && !(row.dispatched ?? 0) };
    }
    const finished = ['RECEIVED', 'CANCELLED', 'COMPLETED'].includes(row.status);
    return { label: 'Cancel line', available: !finished };
}

/**
 * What the send button should say, and whether it should be there at all.
 *
 * A cancelled order cannot be issued, and in-house production has nobody to send to — offering
 * a button that the server will refuse is worse than offering none.
 */
export function resolveSendAction(
    row: ProductionOrderGridRow,
    isVendorOrder: boolean,
): { label: string; available: boolean } {
    if (row.kind !== 'order' || !isVendorOrder || row.status === 'CANCELLED') {
        return { label: '', available: false };
    }
    return { label: row.sentAt ? 'Resend' : 'Send to manufacturer', available: true };
}

/**
 * The same rows, filed under their product instead of their order.
 *
 * THE ONLY GROUPING WHERE A SKU SPANS MANUFACTURERS. The site-level grids answer "what does
 * THIS site owe"; this one is the brand's whole book, so a product here gathers orders placed
 * with different people. That is the question this grouping exists for — how much of a thing is
 * outstanding, everywhere — and it cannot be answered on any other screen.
 *
 * ONE LEVEL, NOT TWO. A product could nest into manufacturers and those into orders, but
 * `subject` already names the manufacturer on every row and a second tier would be deeper than
 * anything else in the grid. The manufacturer is a column here, not a level.
 *
 * A REARRANGEMENT, NOT A SECOND BUILD — `groupLinesBy` regroups the very line rows the order
 * view renders, so the two groupings cannot disagree about a figure.
 */
export function toSkuProductionOrderRows(
    rows: ProductionOrderGridRow[],
): ProductionOrderGridRow[] {
    return groupLinesBy(
        rows,
        (line) => line.productId,
        /*
         * The line becomes the ORDER's row: it takes the order's code, the manufacturer that
         * `subject` held above it, and the facts the Document column needs.
         *
         * `orderId` comes down too, which is safe: the cancel column reads `orderId` only for
         * `kind: 'order'` and a line still cancels by its own `workOrderId`. `status` stays the
         * LINE's, which is the more precise fact and what the column is labelled for.
         */
        (line, order) => ({
            ...line,
            code: order.code,
            subject: order.subject,
            orderDate: order.orderDate,
            orderId: order.orderId,
            hasDocument: order.hasDocument,
            documentGeneratedAt: order.documentGeneratedAt ?? null,
            sentAt: order.sentAt,
            sentTo: order.sentTo,
        }),
    ).map(({ key, lines }) => {
        const [first] = lines;
        const makers = [...new Set(lines.map((line) => line.subject))];
        const dueDates = lines
            .map((line) => line.dueDate)
            .filter((date): date is string => !!date);

        return {
            key: `sku:${key}`,
            kind: 'order' as const,
            // The product heads the group; the order codes sit under it. The column renames to
            // "Product / Order" in this mode.
            code: first?.productName ?? key,
            /*
             * NAMED WHEN THERE IS ONE, counted when there are several. "SportsTex Apparel" is
             * the useful answer for a SKU made in one place, and most are; a count is only
             * better than a name when a name would be a lie.
             */
            subject:
                makers.length === 1
                    ? (makers[0] ?? '')
                    : `${makers.length} manufacturers`,
            productionLocationId: makers.length === 1 ? (first?.productionLocationId ?? '') : '',
            // Earliest, because that is the one that decides what has to move first.
            dueDate: dueDates.length > 0 ? dueDates.reduce((a, b) => (a < b ? a : b)) : null,
            /*
             * BLANK, all of them: none is true of a product wanted by several orders. It was
             * not placed on one date, has no single status, and adding order values together
             * would invent a figure nobody agreed.
             */
            orderDate: null,
            total: null,
            currency: null,
            status: '',
            quantity: sumRows(lines, (line) => line.quantity),
            dispatched: sumRows(lines, (line) => line.dispatched),
            qcApproved: sumRows(lines, (line) => line.qcApproved),
            qcPending: sumRows(lines, (line) => line.qcPending),
            // Already floored per line; summing the floored figures is what stops an over-made
            // SKU on one order cancelling a shortfall on another.
            openQuantity: sumRows(lines, (line) => line.openQuantity),
            // The column reads "Orders" in this mode — how many want this product.
            skuCount: lines.length,
            productId: key,
            productName: first?.productName ?? key,
            /*
             * NO DOCUMENT AND NO ID on a product row. There is no one order behind it to open,
             * send or cancel, and the Document column already returns nothing without an
             * `orderId` — so the buttons drop out on their own rather than by a special case.
             */
            sentAt: null,
            sentTo: null,
            hasDocument: false,
            documentGeneratedAt: null,
            orderId: null,
            workOrderId: null,
            searchBlob: lines.map((line) => `${line.code} ${line.workOrderId ?? ''}`).join(' '),
            lines,
        };
    });
}

/**
 * Whether an order can still be edited in place.
 *
 * OPEN UNTIL THE MANUFACTURER ANSWERS. A draft nobody holds, and an issued order no site has yet
 * acknowledged or started, are both still the brand's to change. The moment any line has moved
 * past ISSUED - acknowledged by hand, or started, which acknowledges by itself - somebody may be
 * cutting to the document, and the whole order locks; one answered SKU locks the rest, because
 * the site read one document. The server refuses those; the button is not offered so nobody
 * finds that out by clicking. Lines are never edited from the grid: a SKU is changed on the
 * order it belongs to.
 */
export function resolveEditAction(row: {
    kind: 'order' | 'line';
    status: string;
    sentAt: string | null;
    /** The live SKU lines under an order row; absent on a line. */
    lines?: Array<{ status: string }>;
}): { label: string; available: boolean } {
    const open = (status: string) => status === 'DRAFT' || status === 'ISSUED';
    const available =
        row.kind === 'order'
        && open(row.status)
        && (row.lines ?? []).every((line) => open(line.status));
    return { label: 'Edit', available };
}

/**
 * Whether the manufacturer holds an older version than the one stored.
 *
 * The PDF is regenerated on every edit to an issued order but not re-sent - a save is not the
 * moment to decide who is told - so a render newer than the send is the durable record that a
 * resend is owed.
 */
export function editedSinceSent(row: {
    sentAt: string | null;
    documentGeneratedAt: string | null;
}): boolean {
    return Boolean(row.sentAt && row.documentGeneratedAt && row.documentGeneratedAt > row.sentAt);
}
